import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiError, rupiah, SERAGAM } from "@/lib/api";
import { PageHeader, Empty, StatusBadge } from "@/components/common";
import { usePaged, Pager } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Receipt, Plus, Pencil, CalendarClock, CheckCircle2, RotateCcw, Tag, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { DateField } from "@/components/DateField";
import { BillAuditDialog } from "@/components/BillAuditDialog";

const MANUAL_FEES = [
  { value: "kitab", label: "Biaya Kitab" },
  { value: "bsb", label: "Biaya Santri Baru (BSB)" },
  { value: "seragam", label: "Biaya Seragam Santri" },
  { value: "kegiatan", label: "Biaya Kegiatan Santri" },
];

export default function KeuTagihan() {
  const [bills, setBills] = useState([]);
  const [students, setStudents] = useState([]);
  const [angkatans, setAngkatans] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("name");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("bulk");
  const [edit, setEdit] = useState(null);
  const [feeTypes, setFeeTypes] = useState([]);
  const [ftOpen, setFtOpen] = useState(false);
  const [ftForm, setFtForm] = useState({ id: null, name: "", default_amount: "" });
  const [editAmt, setEditAmt] = useState("");
  const [editLabel, setEditLabel] = useState("");
  const [editDue, setEditDue] = useState("");
  const [form, setForm] = useState({
    fee_type: "kitab", label: "", amount: "", due_date: "", gender: "all", angkatan: "all",
    student_id: "", installment_allowed: false,
  });

  const load = () => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/admin/bills${q}`).then((r) => setBills(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    api.get("/admin/students").then((r) => setStudents(r.data)).catch(() => {});
    api.get("/admin/angkatans").then((r) => setAngkatans(r.data)).catch(() => {});
    loadFeeTypes();
  }, []);

  const loadFeeTypes = () => api.get("/admin/fee-types").then((r) => setFeeTypes(r.data)).catch(() => {});

  const onFeeChange = (v) => {
    if (v.startsWith("ft:")) {
      const ft = feeTypes.find((f) => `ft:${f.id}` === v);
      setForm({ ...form, fee_type: v, label: ft ? ft.name : "", amount: ft && ft.default_amount ? String(ft.default_amount) : form.amount });
    } else {
      setForm({ ...form, fee_type: v, label: "" });
    }
  };

  const saveFeeType = async () => {
    const name = ftForm.name.trim();
    if (!name) return toast.error("Nama jenis biaya wajib diisi");
    try {
      if (ftForm.id) await api.put(`/admin/fee-types/${ftForm.id}`, { name, default_amount: parseFloat(ftForm.default_amount) || 0 });
      else await api.post("/admin/fee-types", { name, default_amount: parseFloat(ftForm.default_amount) || 0 });
      toast.success("Jenis biaya disimpan");
      setFtForm({ id: null, name: "", default_amount: "" });
      loadFeeTypes();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteFeeType = async (id) => {
    if (!window.confirm("Hapus jenis biaya ini?")) return;
    try {
      await api.delete(`/admin/fee-types/${id}`);
      toast.success("Jenis biaya dihapus");
      loadFeeTypes();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.due_date) return toast.error("Pilih tanggal jatuh tempo");
    setSaving(true);
    try {
      const feeTypeValue = form.fee_type.startsWith("ft:")
        ? (feeTypes.find((f) => `ft:${f.id}` === form.fee_type)?.name || form.fee_type)
        : form.fee_type;
      const payload = {
        fee_type: feeTypeValue, label: form.label, amount: parseFloat(form.amount),
        due_date: form.due_date, installment_allowed: form.installment_allowed,
        installments: form.installment_allowed ? 3 : 1,
      };
      if (mode === "bulk") {
        payload.gender = form.gender === "all" ? null : form.gender;
        payload.angkatan = form.angkatan === "all" ? null : parseInt(form.angkatan);
        const { data } = await api.post("/admin/bills/bulk", payload);
        toast.success(`${data.created} tagihan dibuat`);
      } else {
        if (!form.student_id) { setSaving(false); return toast.error("Pilih santri"); }
        payload.student_id = form.student_id;
        await api.post("/admin/bills/single", payload);
        toast.success("Tagihan dibuat");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/bills/${edit.id}`, {
        total_amount: parseFloat(editAmt),
        label: editLabel,
        due_date: editDue,
      });
      toast.success("Tagihan diperbarui");
      setEdit(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openEdit = (b) => {
    setEdit(b);
    setEditAmt(String(b.total_amount));
    setEditLabel(b.label || "");
    setEditDue(b.due_date || "");
  };

  const markPaid = async (b) => {
    if (!window.confirm(`Tandai LUNAS tagihan "${b.label}" milik ${b.student_name}? Ini untuk pembayaran tunai/manual di luar aplikasi.`)) return;
    try {
      await api.post(`/admin/bills/${b.id}/mark-paid`);
      toast.success("Tagihan ditandai lunas (manual)");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const unmarkPaid = async (b) => {
    if (!window.confirm(`Batalkan status lunas manual tagihan "${b.label}"? Status akan dikembalikan seperti sebelum ditandai lunas.`)) return;
    try {
      await api.post(`/admin/bills/${b.id}/unmark-paid`);
      toast.success("Status lunas manual dibatalkan");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteBill = async (b) => {
    let msg = `Hapus tagihan "${b.label}" milik ${b.student_name}? Tindakan ini tidak dapat dibatalkan.`;
    if (b.paid_amount > 0) msg += `\n\nPERHATIAN: tagihan ini sudah dibayar sebesar ${rupiah(b.paid_amount)}. Menghapus tidak mengembalikan/menghapus catatan pembayaran.`;
    if (b.auto) msg += `\n\nCatatan: ini tagihan bulanan otomatis. Jika periode ini masih berjalan, tagihan dapat dibuat ulang otomatis.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/bills/${b.id}`);
      toast.success("Tagihan dihapus");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const isSeragam = form.fee_type === "seragam";

  const view = useMemo(() => {
    const s = search.trim().toLowerCase();
    let list = s ? bills.filter((b) => (b.student_name || "").toLowerCase().includes(s)) : bills.slice();
    const byName = (a, b) => (a.student_name || "").localeCompare(b.student_name || "", "id");
    if (sort === "name") {
      list.sort(byName);
    } else if (sort === "angkatan") {
      list.sort((a, b) => (b.student_angkatan || 0) - (a.student_angkatan || 0) || byName(a, b));
    } else if (sort === "unit") {
      const ord = { putra: 0, putri: 1 };
      list.sort((a, b) => (ord[a.student_gender] ?? 2) - (ord[b.student_gender] ?? 2) || byName(a, b));
    }
    return list;
  }, [bills, search, sort]);

  const pg = usePaged(view, 10);

  return (
    <div>
      <PageHeader
        title="Tagihan"
        subtitle="Biaya bulanan tertagih otomatis tiap tanggal 1. Buat tagihan lain di sini; nominal bisa diedit."
        action={
          <div className="flex gap-2">
          <BillAuditDialog onFixed={load} />
          <Button variant="outline" onClick={() => { setFtForm({ id: null, name: "", default_amount: "" }); setFtOpen(true); }} data-testid="manage-feetypes-button" className="rounded-xl"><Tag size={16} className="mr-1.5" /> Kelola Jenis Biaya</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-bill-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
                <Plus size={18} className="mr-2" /> Buat Tagihan
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">Buat Tagihan Baru</DialogTitle></DialogHeader>
              <Tabs value={mode} onValueChange={setMode} className="mt-2">
                <TabsList className="grid grid-cols-2 w-full">
                  <TabsTrigger value="bulk" data-testid="tab-bulk">Massal</TabsTrigger>
                  <TabsTrigger value="single" data-testid="tab-single">Per Santri</TabsTrigger>
                </TabsList>
              </Tabs>
              <form onSubmit={submit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Jenis Biaya</Label>
                  <Select value={form.fee_type} onValueChange={onFeeChange}>
                    <SelectTrigger data-testid="bill-feetype-select" className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MANUAL_FEES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                      {feeTypes.map((f) => <SelectItem key={f.id} value={`ft:${f.id}`}>{f.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {isSeragam ? (
                  <div className="space-y-2">
                    <Label>Jenis Seragam</Label>
                    <Select value={form.label} onValueChange={(v) => setForm({ ...form, label: v })}>
                      <SelectTrigger data-testid="bill-seragam-select" className="rounded-xl"><SelectValue placeholder="Pilih seragam" /></SelectTrigger>
                      <SelectContent>
                        {SERAGAM.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Keterangan Tagihan</Label>
                    <Input data-testid="bill-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} required className="rounded-xl" placeholder="cth: Biaya Kitab 2026/2027" />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Nominal (Rp)</Label>
                    <Input data-testid="bill-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label>Jatuh Tempo</Label>
                    <DateField value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} testid="bill-duedate-input" />
                  </div>
                </div>
                {mode === "bulk" ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Untuk Unit</Label>
                      <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                        <SelectTrigger data-testid="bill-unit-select" className="rounded-xl"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Semua</SelectItem>
                          <SelectItem value="putra">Putra</SelectItem>
                          <SelectItem value="putri">Putri</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Angkatan</Label>
                      <Select value={form.angkatan} onValueChange={(v) => setForm({ ...form, angkatan: v })}>
                        <SelectTrigger data-testid="bill-angkatan-select" className="rounded-xl"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Semua Angkatan</SelectItem>
                          {angkatans.map((a) => <SelectItem key={a} value={String(a)}>Angkatan {a}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Santri</Label>
                    <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                      <SelectTrigger data-testid="bill-student-select" className="rounded-xl"><SelectValue placeholder="Pilih santri" /></SelectTrigger>
                      <SelectContent>
                        {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.gender})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex items-center justify-between rounded-xl bg-stone-50 border p-3">
                  <div>
                    <Label className="cursor-pointer">Bisa Dicicil (3x)</Label>
                    <p className="text-xs text-stone-400">Untuk biaya kitab & BSB</p>
                  </div>
                  <Switch data-testid="bill-installment-switch" checked={form.installment_allowed} onCheckedChange={(v) => setForm({ ...form, installment_allowed: v })} />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving} data-testid="save-bill-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
                    {saving ? "Menyimpan…" : "Buat Tagihan"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <div className="flex items-center gap-2 mb-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5 w-fit">
        <CalendarClock size={16} /> Tagihan bulanan dibuat otomatis tiap awal bulan (jatuh tempo tanggal 6).
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList>
          <TabsTrigger value="all">Semua</TabsTrigger>
          <TabsTrigger value="unpaid">Belum Bayar</TabsTrigger>
          <TabsTrigger value="partial">Cicilan</TabsTrigger>
          <TabsTrigger value="paid">Lunas</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="bill-search" placeholder="Cari nama santri…" className="rounded-xl bg-white h-11 pl-9" />
        </div>
        <div className="sm:w-64">
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="rounded-xl bg-white h-11" data-testid="bill-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Urutkan: Nama (A–Z)</SelectItem>
              <SelectItem value="angkatan">Urutkan: Angkatan (terbaru → terlama)</SelectItem>
              <SelectItem value="unit">Urutkan: Unit (Putra → Putri)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-x-auto">
        {view.length === 0 ? (
          <Empty><Receipt className="mx-auto mb-2" /> {search.trim() ? "Tidak ada tagihan untuk nama tersebut." : "Belum ada tagihan."}</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Santri</TableHead>
                <TableHead>Tagihan</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Dibayar</TableHead>
                <TableHead>Jatuh Tempo</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageRows.map((b) => (
                <TableRow key={b.id} data-testid={`bill-row-${b.id}`}>
                  <TableCell className="font-medium text-stone-800">{b.student_name}</TableCell>
                  <TableCell>
                    {b.label}
                    {b.auto && <span className="ml-2 text-xs text-emerald-600">otomatis</span>}
                    {b.installment_allowed && <span className="ml-2 text-xs text-amber-600">cicilan</span>}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">{rupiah(b.total_amount)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{rupiah(b.paid_amount)}</TableCell>
                  <TableCell className="text-sm">{b.due_date}</TableCell>
                  <TableCell><StatusBadge status={b.status} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => openEdit(b)} data-testid={`edit-bill-${b.id}`} className="rounded-lg">
                      <Pencil size={14} className="mr-1" /> Edit
                    </Button>
                    {b.status !== "paid" && (
                      <Button size="sm" onClick={() => markPaid(b)} data-testid={`markpaid-bill-${b.id}`} className="ml-1 rounded-lg bg-emerald-600 hover:bg-emerald-700">
                        <CheckCircle2 size={14} className="mr-1" /> Lunas
                      </Button>
                    )}
                    {b.manual_paid_by && (
                      <Button size="sm" variant="outline" onClick={() => unmarkPaid(b)} data-testid={`unmarkpaid-bill-${b.id}`} className="ml-1 rounded-lg text-rose-600 border-rose-200">
                        <RotateCcw size={14} className="mr-1" /> Batalkan Lunas
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => deleteBill(b)} data-testid={`delete-bill-${b.id}`} className="ml-1 rounded-lg text-rose-600 border-rose-200">
                      <Trash2 size={14} className="mr-1" /> Hapus
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Pager {...pg} />
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Edit Tagihan</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Keterangan Tagihan</Label>
              <Input data-testid="edit-bill-label-input" value={editLabel} onChange={(e) => setEditLabel(e.target.value)} className="rounded-xl" placeholder="cth: Biaya Kitab 2026/2027" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Total Tagihan (Rp)</Label>
                <Input type="number" data-testid="edit-bill-amount-input" value={editAmt} onChange={(e) => setEditAmt(e.target.value)} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>Jatuh Tempo</Label>
                <DateField value={editDue} onChange={setEditDue} testid="edit-bill-duedate-input" />
              </div>
            </div>
            <p className="text-xs text-stone-400">Sudah dibayar: {rupiah(edit?.paid_amount || 0)}</p>
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} data-testid="save-edit-bill-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ftOpen} onOpenChange={setFtOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Kelola Jenis Biaya</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="rounded-xl border border-stone-200 p-3 space-y-3">
              <p className="text-sm font-medium text-stone-700">{ftForm.id ? "Ubah Jenis Biaya" : "Tambah Jenis Biaya Baru"}</p>
              <div className="space-y-2">
                <Label>Nama Jenis Biaya</Label>
                <Input data-testid="feetype-name-input" value={ftForm.name} onChange={(e) => setFtForm({ ...ftForm, name: e.target.value })} className="rounded-xl" placeholder="cth: Denda Pelanggaran Peraturan" />
              </div>
              <div className="space-y-2">
                <Label>Nominal Default (Rp, opsional)</Label>
                <Input type="number" data-testid="feetype-amount-input" value={ftForm.default_amount} onChange={(e) => setFtForm({ ...ftForm, default_amount: e.target.value })} className="rounded-xl" placeholder="cth: 50000" />
              </div>
              <div className="flex gap-2">
                <Button onClick={saveFeeType} data-testid="save-feetype-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">{ftForm.id ? "Simpan Perubahan" : "Tambah"}</Button>
                {ftForm.id && <Button variant="outline" onClick={() => setFtForm({ id: null, name: "", default_amount: "" })} className="rounded-xl">Batal Ubah</Button>}
              </div>
            </div>
            <div className="space-y-2">
              {feeTypes.length === 0 ? (
                <p className="text-sm text-stone-400 text-center py-3">Belum ada jenis biaya kustom.</p>
              ) : feeTypes.map((f) => (
                <div key={f.id} data-testid={`feetype-item-${f.id}`} className="flex items-center justify-between rounded-xl border border-stone-200 px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-stone-800">{f.name}</p>
                    {f.default_amount > 0 && <p className="text-xs text-stone-400 font-mono">{rupiah(f.default_amount)}</p>}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => setFtForm({ id: f.id, name: f.name, default_amount: String(f.default_amount || "") })} data-testid={`edit-feetype-${f.id}`} className="rounded-lg"><Pencil size={13} /></Button>
                    <Button size="sm" variant="outline" onClick={() => deleteFeeType(f.id)} data-testid={`delete-feetype-${f.id}`} className="rounded-lg text-rose-600 border-rose-200"><Trash2 size={13} /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
